import { useEffect, useRef } from 'react'
import { useToast } from '../components/Toast.tsx'
import { api } from './api.ts'

/**
 * Tells whoever has a verification open what the ERP wrote after it was
 * read, and offers to act on it: a pote (#558), a label printed after the
 * analysis that changes a posology nobody has answered (#614, ADR 0040), or a
 * quotation read before approval that has since been approved or changed
 * (#437, ADR 0046).
 *
 * Opening the screen is also what ends acting on these by itself: from the
 * first open the verification is the person's, and nothing changes under
 * them. So this says it instead, once each, with the action one press away.
 *
 * One beat for both, asking our own server, which already learns of a new
 * pote and of a new label on every poll pass. Nothing here reaches the
 * pharmacy's database.
 */

/** The poll finds a new pote within thirty seconds; asking faster learns nothing. */
export const LATE_ERP_POLL_MS = 30_000

export const LATE_POTE_MESSAGE = 'O ERP recebeu um pote novo depois desta leitura.'
export const LATE_POTE_ACTION = 'Ler de novo'

export const QUOTATION_APPROVED_MESSAGE = 'O orçamento foi aprovado e virou requisição.'
export const QUOTATION_CHANGED_MESSAGE = 'O orçamento foi alterado depois desta leitura.'

export const LATE_LABEL_MESSAGE = 'Um rótulo foi impresso depois desta análise.'
export const LATE_LABEL_ACTION = 'Comparar posologia'
export const LATE_LABEL_COMPARING = 'Comparando a posologia com o rótulo…'
export const LATE_LABEL_SLOW = 'A comparação está demorando. A posologia muda aqui quando terminar.'
export const LATE_LABEL_DONE = 'Posologia comparada de novo com o rótulo impresso.'
export const LATE_LABEL_FAILED = 'Não foi possível comparar a posologia agora.'

/**
 * Whether an answer is news: a pote arrived, and it is not the one already
 * said. Between the press and the new read being recorded the server still
 * answers with the same pote, and saying it twice would invite a second press.
 */
export function isNewLatePote(
  answer: { arrived: boolean; seenAt: string | null },
  announced: string | undefined,
): boolean {
  return answer.arrived && answer.seenAt !== null && answer.seenAt !== announced
}

/**
 * The same for the quotation: what to say about it now, if anything, and the
 * key that says it once. Approved outranks changed -- reading again reads the
 * requisition, which carries the change -- and a second change after the
 * first was said is a new one, said again.
 */
export function lateQuotationNews(
  answer: { approved: boolean; changed: boolean; changedAt: string | null } | undefined,
): { key: string; message: string } | undefined {
  if (answer === undefined) return undefined
  if (answer.approved) return { key: 'approved', message: QUOTATION_APPROVED_MESSAGE }
  if (answer.changed) {
    return { key: `changed:${answer.changedAt ?? ''}`, message: QUOTATION_CHANGED_MESSAGE }
  }
  return undefined
}

/**
 * The same for labels: news when some posology is waiting and it is not the
 * set already said. One label more, for another preparation, is said again.
 */
export function lateLabelKey(pending: readonly string[]): string | undefined {
  return pending.length === 0 ? undefined : [...pending].sort().join(',')
}

/**
 * @param reread asks for the verification to be read again, as the screen's own refresh does.
 * @param reload reads what is stored again, for a posology that was compared again.
 */
export function useLateErp(verificationId: string, reread: () => void, reload: () => void): void {
  const toast = useToast()
  // The latest of each, without restarting the poll each time the screen renders.
  const rereadRef = useRef(reread)
  rereadRef.current = reread
  const reloadRef = useRef(reload)
  reloadRef.current = reload
  const toastRef = useRef(toast)
  toastRef.current = toast

  useEffect(() => {
    let live = true
    let announcedPote: string | undefined
    let announcedLabel: string | undefined
    let announcedQuotation: string | undefined
    // A press whose re-judge outlived the request: its end is seen here.
    let comparing = false

    const compared = () => {
      reloadRef.current()
      toastRef.current.show(LATE_LABEL_DONE, { tone: 'success' })
    }

    const compare = async () => {
      toastRef.current.show(LATE_LABEL_COMPARING, { tone: 'info' })
      try {
        const outcome = await api.rejudgePosology(verificationId)
        if (!live) return
        if (outcome.status === 'done') compared()
        else {
          comparing = true
          toastRef.current.show(LATE_LABEL_SLOW, { tone: 'info' })
        }
      } catch {
        if (!live) return
        // Offered again on the next beat: nothing was written.
        announcedLabel = undefined
        toastRef.current.show(LATE_LABEL_FAILED, { tone: 'error' })
      }
    }

    // Best-effort, like the claim: a screen that could not say it was opened
    // is still a screen somebody should be able to read.
    void api.markOpened(verificationId).catch(() => undefined)

    const ask = async () => {
      try {
        const answer = await api.lateErp(verificationId)
        if (!live) return

        if (isNewLatePote(answer.pote, announcedPote)) {
          announcedPote = answer.pote.seenAt ?? undefined
          toastRef.current.show(LATE_POTE_MESSAGE, {
            tone: 'warning',
            action: { label: LATE_POTE_ACTION, onSelect: () => rereadRef.current() },
          })
        }

        const quotation = lateQuotationNews(answer.quotation)
        if (quotation !== undefined && quotation.key !== announcedQuotation) {
          announcedQuotation = quotation.key
          toastRef.current.show(quotation.message, {
            tone: 'warning',
            action: { label: LATE_POTE_ACTION, onSelect: () => rereadRef.current() },
          })
        }

        const key = lateLabelKey(answer.label.pending)
        if (comparing && key === undefined) {
          comparing = false
          compared()
        } else if (!comparing && key !== undefined && key !== announcedLabel) {
          announcedLabel = key
          toastRef.current.show(LATE_LABEL_MESSAGE, {
            tone: 'warning',
            action: { label: LATE_LABEL_ACTION, onSelect: () => void compare() },
          })
        }
      } catch {
        // Asked again on the next beat.
      }
    }

    void ask()
    const beat = setInterval(() => void ask(), LATE_ERP_POLL_MS)
    return () => {
      live = false
      clearInterval(beat)
    }
  }, [verificationId])
}
